import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import T from 'i18n-react';
import dynamic from 'next/dynamic';
import { shallow } from 'zustand/shallow';

import { Select } from '@motorway/mw-highway-code/pre-alpha';

import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { triggerSortDropdownExpandedEvent, triggerSortOrderSelectedEvent } from 'Services/analytics/events';
import { useTriggerSortOrderChangedFromRecommended } from 'Services/optimizely/events/useTriggerSortOrderChangedFromRecommended';
import useVehicleFilterStore from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { applyCypressData } from 'Utilities/helpers';
import useSortOrder from 'Utilities/hooks/vehicleList/useSortOrder/useSortOrder';

import { arrayNoEmptyValue } from '../VehicleList.helpers';

import styles from './VehicleSort.module.scss';

T.setTexts(require('../../texts.json'));

const Options = ({ children, dealerPostcode }) =>
	children.map(({ name, value }) => (
		<option key={value} value={value}>
			{T.translate(`vehicleList.sorts.${name}`, { value: dealerPostcode })}
		</option>
	));

const VehicleSort = ({ className: selectClasses, cypressId, dealerLatLng, dealerPostcode, selected = '' }) => {
	const {
		minWidth: { breakpointContent: isDesktop },
	} = useBreakpoint();
	const { defaultOrder, defaultSort, sortOptions } = useSortOrder();
	const triggerSortOrderChangedFromRecommended = useTriggerSortOrderChangedFromRecommended();

	const [setValue, setHasUserSetSort, config] = useVehicleFilterStore(
		(store) => [store.setValue, store.setHasUserSetSort, store.config],
		shallow,
	);
	const vehicleSortOptions = arrayNoEmptyValue(dealerLatLng)
		? sortOptions
		: sortOptions.filter((u) => u.name !== 'nearestTo');

	const onChangeHandler = (e) => {
		const value = e?.target?.value;
		const selectedVal = vehicleSortOptions.find((u) => u.value === value);

		triggerSortOrderChangedFromRecommended({ currentSortOrder: selected, newSortOrder: selectedVal?.value });
		triggerSortOrderSelectedEvent({ sortOrderValue: selectedVal?.value });
		// eslint-disable-next-line no-unsafe-optional-chaining
		const [sort, order] = selectedVal?.value?.split('-');
		setValue({ id: config.order.id, value: order });
		setValue({ id: config.sort.id, value: sort });
		setHasUserSetSort(sort !== defaultSort || order !== defaultOrder);
	};

	return (
		<div className={cx(styles.wrapper, selectClasses)}>
			<Select
				data-thc-select
				fullWidth
				formProps={{
					input: {
						onChange: onChangeHandler,
						onClick: triggerSortDropdownExpandedEvent,
						...applyCypressData(cypressId),
						value: selected,
					},
				}}
				id="vehicleSort"
				label={T.translate('vehicleList.sortOrderLabel')}
				showLabel={!isDesktop}
			>
				<Options dealerPostcode={dealerPostcode}>{vehicleSortOptions}</Options>
			</Select>
		</div>
	);
};

VehicleSort.defaultProps = {
	className: null,
	dealerPostcode: null,
	selected: undefined,
};

VehicleSort.propTypes = {
	className: PropTypes.string,
	cypressId: PropTypes.string,
	dealerLatLng: PropTypes.arrayOf(PropTypes.number).isRequired,
	dealerPostcode: PropTypes.string,
	selected: PropTypes.string,
};

export default dynamic(() => Promise.resolve(VehicleSort), {
	ssr: false,
});
